import React, { useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import {
  FormatAlignCenter,
  FormatAlignLeft,
  VerticalAlignBottom,
  ViewStream,
  ViewColumn,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { AdminIconBar } from "../icon-bar/AdminIconBar";
import { AdminIconBarItem } from "../icon-bar/AdminIconBarItem";
import { updateSection } from "../Admin/service";
import {
  useScrollyTellProps,
  useScrollytellTranslation,
} from "../Admin/scrollytellContext";
import { useClickOutside } from "../common/useClickOutside";
import { SectionTemplates } from "../helpers/enums";
import {
  applyTitlePreset,
  applyBodyTwoColumnPreset,
  applyBodyOneColumnPreset,
} from "../Admin/templatePresets";

const useStyles = createUseStyles({
  presetMenu: {
    top: "41px",
    left: "2px",
    position: "absolute",
    display: "inline-flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px",
    backgroundColor: "#FFFFFF",
    boxShadow:
      "0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px rgba(0, 0, 0, 0.14), 0px 1px 14px rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
    zIndex: 1200,
  },
  presetMenuItem: {
    "& svg": {
      fill: "#00000099",
    },
    "&:hover svg": {
      fill: "#3F51B5",
    },
  },
});

export const TemplatePresetSubMenu = ({ section, sectionId, onClose }) => {
  const t = useScrollytellTranslation();
  const presetMenuRef = useRef(null);
  const { story, onChange } = useScrollyTellProps();
  const classes = useStyles();
  const [confirmOpen, setConfirmOpen] = useState(false);

  useClickOutside(presetMenuRef, () => {
    if (!confirmOpen && onClose) onClose();
  });

  const handleApplyPreset = (updatedSection) => {
    onChange(updateSection(story, sectionId, updatedSection));
    if (onClose) onClose();
  };

  const handleOneColumnClick = () => {
    if (section?.containers?.length > 1) {
      setConfirmOpen(true);
    } else {
      handleApplyPreset(applyBodyOneColumnPreset(section));
    }
  };

  const isTitleSection =
    section?.templateId === SectionTemplates.TITLE_SECTION ||
    section?.templateId === 1;

  const isBodySection =
    section?.templateId === SectionTemplates.BODYTEXT_SECTION ||
    section?.templateId === 2;

  if (!isTitleSection && !isBodySection) {
    return null;
  }

  return (
    <>
      <AdminIconBar ref={presetMenuRef} className={classes.presetMenu}>
        {isTitleSection && (
          <>
            <AdminIconBarItem
              Icon={FormatAlignCenter}
              onClick={() => handleApplyPreset(applyTitlePreset(section, "centered"))}
              tooltip={t("templatePresetSubMenu.titleCentered", "Sentrert tittel")}
              classname={classes.presetMenuItem}
              data-testid="preset-title-centered"
            />
            <AdminIconBarItem
              Icon={FormatAlignLeft}
              onClick={() => handleApplyPreset(applyTitlePreset(section, "left-hero"))}
              tooltip={t("templatePresetSubMenu.titleLeftHero", "Venstrestilt hero")}
              classname={classes.presetMenuItem}
              data-testid="preset-title-left-hero"
            />
            <AdminIconBarItem
              Icon={VerticalAlignBottom}
              onClick={() => handleApplyPreset(applyTitlePreset(section, "bottom-split"))}
              tooltip={t("templatePresetSubMenu.titleBottomSplit", "Bunn-justert tittel")}
              classname={classes.presetMenuItem}
              data-testid="preset-title-bottom-split"
            />
          </>
        )}

        {isBodySection && (
          <>
            <AdminIconBarItem
              Icon={ViewStream}
              onClick={handleOneColumnClick}
              tooltip={t("templatePresetSubMenu.bodyOneColumn", "Enkel spalte (1 kolonne)")}
              classname={classes.presetMenuItem}
              data-testid="preset-body-one-column"
            />
            <AdminIconBarItem
              Icon={ViewColumn}
              onClick={() => handleApplyPreset(applyBodyTwoColumnPreset(section))}
              tooltip={t("templatePresetSubMenu.bodyTwoColumns", "Tospaltet oppsett (2 kolonner)")}
              classname={classes.presetMenuItem}
              data-testid="preset-body-two-columns"
            />
          </>
        )}
      </AdminIconBar>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-one-column-title"
      >
        <DialogTitle id="confirm-one-column-title">
          {t("templatePresetSubMenu.confirmTitle", "Bytte til 1 kolonne?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              "templatePresetSubMenu.confirmText",
              "Bytte til 1 kolonne vil slette kolonne 2 og innholdet i den. Vil du fortsette?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t("common.cancel", "Avbryt")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmOpen(false);
              handleApplyPreset(applyBodyOneColumnPreset(section));
            }}
            data-testid="confirm-one-column-proceed"
          >
            {t("common.continue", "Fortsett")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
